/**
 * Member Compliance History
 * Detailed view of all work hour submissions and their verification status
 * Trust Marketplace - Work Documentation System
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  Briefcase,
  GraduationCap,
  Heart,
  Users
} from 'lucide-react';

interface MemberHistoryProps {
  memberId: number;
  enrollmentId: number;
}

interface WorkSubmission {
  id: number;
  month: number;
  year: number;
  total_hours: number;
  employment_hours: number;
  education_hours: number;
  volunteer_hours: number;
  job_training_hours: number;
  job_search_hours: number;
  community_service_hours: number;
  employer_name: string | null;
  education_institution: string | null;
  volunteer_organization: string | null;
  meets_requirement: boolean;
  verification_status: string;
  submitted_date: string | null;
  verified_date: string | null;
  verified_by_name: string | null;
  verification_notes: string | null;
  rejection_reason: string | null;
  document_count: number;
  milestones_triggered: any[];
  incentive_earned: boolean;
  incentive_amount: number;
}

const MemberComplianceHistory: React.FC<MemberHistoryProps> = ({
  memberId,
  enrollmentId
}) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [submissions, setSubmissions] = useState<WorkSubmission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<WorkSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    fetchHistory();
  }, [enrollmentId]);

  useEffect(() => {
    applyFilters();
  }, [submissions, statusFilter, yearFilter]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const url = getWeaveinApiUrl(`/api/work-compliance/history?enrollment_id=${enrollmentId}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch history: ${response.status}`);
      }

      const result = await response.json();
      setSubmissions(result.submissions || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...submissions];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(s => s.verification_status === statusFilter);
    }

    if (yearFilter) {
      filtered = filtered.filter(s => s.year === yearFilter);
    }

    // Sort by date descending
    filtered.sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      return b.month - a.month;
    });

    setFilteredSubmissions(filtered);
  };

  const getMonthName = (month: number) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  const getStatusBadge = (status: string, compliant: boolean) => {
    if (status === 'VERIFIED') {
      return (
        <Badge className={compliant ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
          <CheckCircle2 size={14} className="mr-1" />
          {compliant ? 'Compliant' : 'Verified - Below 80hrs'}
        </Badge>
      );
    }
    if (status === 'REJECTED') {
      return (
        <Badge className="bg-red-100 text-red-800">
          <XCircle size={14} className="mr-1" />
          {t('workcompliance.rejected', 'Rejected')}
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-100 text-blue-800">
        <Clock size={14} className="mr-1" />
        {t('workcompliance.pending_verification', 'Pending Verification')}
      </Badge>
    );
  };

  const downloadSubmissionReport = (submission: WorkSubmission) => {
    const content = [
      `Work Activity Submission Report`,
      `Period: ${getMonthName(submission.month)} ${submission.year}`,
      `Submitted: ${submission.submitted_date ? fmt.short(submission.submitted_date) : 'N/A'}`,
      ``,
      `Hours Breakdown:`,
      `Employment: ${submission.employment_hours} hrs`,
      `Education: ${submission.education_hours} hrs`,
      `Volunteer: ${submission.volunteer_hours} hrs`,
      `Job Training: ${submission.job_training_hours} hrs`,
      `Job Search: ${submission.job_search_hours} hrs`,
      `Community Service: ${submission.community_service_hours} hrs`,
      `TOTAL: ${submission.total_hours} hrs`,
      ``,
      `Status: ${submission.verification_status}`,
      submission.employer_name ? `Employer: ${submission.employer_name}` : '',
      submission.verification_notes ? `Notes: ${submission.verification_notes}` : '',
      submission.incentive_earned ? `Incentive Earned: $${submission.incentive_amount}` : ''
    ].filter(Boolean).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `work-submission-${submission.month}-${submission.year}.txt`;
    a.click();
  };

  const downloadFullHistory = () => {
    const csvContent = [
      ['Month', 'Year', 'Total Hours', 'Employment', 'Education', 'Volunteer', 'Training', 'Status', 'Compliant', 'Incentive $'],
      ...filteredSubmissions.map(s => [
        getMonthName(s.month),
        s.year,
        s.total_hours,
        s.employment_hours,
        s.education_hours,
        s.volunteer_hours,
        s.job_training_hours,
        s.verification_status,
        s.meets_requirement ? 'Yes' : 'No',
        s.incentive_amount || 0
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `work-compliance-history-${memberId}.csv`;
    a.click();
  };

  const getYears = () => {
    const years = Array.from(new Set(submissions.map(s => s.year)));
    return years.sort((a, b) => b - a);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card className="p-6">
          <div className="flex items-center text-red-600">
            <XCircle className="mr-2" />
            <span>{error}</span>
          </div>
        </Card>
      </div>
    );
  }

  const stats = {
    total: filteredSubmissions.length,
    compliant: filteredSubmissions.filter(s => s.meets_requirement && s.verification_status === 'VERIFIED').length,
    pending: filteredSubmissions.filter(s => s.verification_status === 'PENDING').length,
    totalIncentives: filteredSubmissions.reduce((sum, s) => sum + (s.incentive_amount || 0), 0)
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          {!tabbed && <h1 className="text-3xl font-bold">{t('workcompliance.work_compliance_history', 'Work Compliance History')}</h1>}
          <p className="text-gray-600 mt-1">{t('workcompliance.complete_record_of_all_work', 'Complete record of all work hour submissions')}</p>
        </div>
        <button
          onClick={downloadFullHistory}
          disabled={filteredSubmissions.length === 0}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 flex items-center gap-2"
        >
          <Download size={16} />
          {t('workcompliance.export_history', 'Export History')}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <Calendar className="text-blue-600" size={24} />
            <span className="text-2xl font-bold">{stats.total}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{t('workcompliance.total_submissions', 'Total Submissions')}</p>
        </Card>

        <Card className="p-4 bg-green-50">
          <div className="flex items-center justify-between">
            <CheckCircle2 className="text-green-600" size={24} />
            <span className="text-2xl font-bold">{stats.compliant}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{t('workcompliance.compliant_months', 'Compliant Months')}</p>
        </Card>

        <Card className="p-4 bg-yellow-50">
          <div className="flex items-center justify-between">
            <Clock className="text-yellow-600" size={24} />
            <span className="text-2xl font-bold">{stats.pending}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{t('workcompliance.pending_verification_1', 'Pending Verification')}</p>
        </Card>

        <Card className="p-4 bg-blue-50">
          <div className="flex items-center justify-between">
            <span className="text-2xl">💰</span>
            <span className="text-2xl font-bold">${stats.totalIncentives.toFixed(2)}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{t('workcompliance.total_incentives', 'Total Incentives')}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={16} />
            <span className="font-semibold">{t('workcompliance.filters', 'Filters:')}</span>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1 border rounded"
          >
            <option value="ALL">{t('workcompliance.all_status', 'All Status')}</option>
            <option value="VERIFIED">{t('workcompliance.verified', 'Verified')}</option>
            <option value="PENDING">{t('workcompliance.pending', 'Pending')}</option>
            <option value="REJECTED">{t('workcompliance.rejected_1', 'Rejected')}</option>
          </select>

          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(parseInt(e.target.value))}
            className="px-3 py-1 border rounded"
          >
            <option value={0}>{t('workcompliance.all_years', 'All Years')}</option>
            {getYears().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <div className="ml-auto text-sm text-gray-600">
            Showing {filteredSubmissions.length} of {submissions.length} submissions
          </div>
        </div>
      </Card>

      {/* Submissions List */}
      <div className="space-y-3">
        {filteredSubmissions.length === 0 ? (
          <Card className="p-8 text-center text-gray-500">
            <p>{t('workcompliance.no_submissions_found_for_the', 'No submissions found for the selected filters')}</p>
          </Card>
        ) : (
          filteredSubmissions.map((submission) => (
            <Card key={submission.id} className="p-6">
              <div
                className="flex items-start justify-between cursor-pointer"
                onClick={() => setExpandedId(expandedId === submission.id ? null : submission.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="text-xl font-bold">
                      {getMonthName(submission.month)} {submission.year}
                    </h3>
                    {getStatusBadge(submission.verification_status, submission.meets_requirement)}
                    {submission.incentive_earned && (
                      <Badge className="bg-purple-100 text-purple-800">
                        💰 ${submission.incentive_amount.toFixed(2)} earned
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <span>{t('workcompliance.total', 'Total:')} <strong>{submission.total_hours} hrs</strong></span>
                    <span>{submission.document_count} document(s)</span>
                    {submission.submitted_date && (
                      <span>Submitted: {fmt.short(submission.submitted_date)}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadSubmissionReport(submission);
                    }}
                    className="p-2 hover:bg-gray-100 rounded"
                    title={t('workcompliance.download_report', 'Download report')}
                  >
                    <Download size={16} />
                  </button>
                  {expandedId === submission.id ? <ChevronUp /> : <ChevronDown />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === submission.id && (
                <div className="mt-4 pt-4 border-t space-y-4">
                  {/* Hours Breakdown */}
                  <div>
                    <h4 className="font-semibold mb-3">{t('workcompliance.hours_breakdown', 'Hours Breakdown')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {submission.employment_hours > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                          <Briefcase size={16} className="text-blue-600" />
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.employment', 'Employment')}</p>
                            <p className="font-bold">{submission.employment_hours} hrs</p>
                          </div>
                        </div>
                      )}
                      {submission.education_hours > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
                          <GraduationCap size={16} className="text-purple-600" />
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.education', 'Education')}</p>
                            <p className="font-bold">{submission.education_hours} hrs</p>
                          </div>
                        </div>
                      )}
                      {submission.volunteer_hours > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-red-50 rounded">
                          <Heart size={16} className="text-red-600" />
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.volunteer', 'Volunteer')}</p>
                            <p className="font-bold">{submission.volunteer_hours} hrs</p>
                          </div>
                        </div>
                      )}
                      {submission.job_training_hours > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                          <Users size={16} className="text-green-600" />
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.job_training', 'Job Training')}</p>
                            <p className="font-bold">{submission.job_training_hours} hrs</p>
                          </div>
                        </div>
                      )}
                      {submission.job_search_hours > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-orange-50 rounded">
                          <Users size={16} className="text-orange-600" />
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.job_search', 'Job Search')}</p>
                            <p className="font-bold">{submission.job_search_hours} hrs</p>
                          </div>
                        </div>
                      )}
                      {submission.community_service_hours > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-teal-50 rounded">
                          <Users size={16} className="text-teal-600" />
                          <div>
                            <p className="text-sm text-gray-600">{t('workcompliance.community_service', 'Community Service')}</p>
                            <p className="font-bold">{submission.community_service_hours} hrs</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Organization Details */}
                  {(submission.employer_name || submission.education_institution || submission.volunteer_organization) && (
                    <div>
                      <h4 className="font-semibold mb-2">{t('workcompliance.organizations', 'Organizations')}</h4>
                      <div className="space-y-1 text-sm">
                        {submission.employer_name && <p>Employer: {submission.employer_name}</p>}
                        {submission.education_institution && <p>School: {submission.education_institution}</p>}
                        {submission.volunteer_organization && <p>Volunteer Org: {submission.volunteer_organization}</p>}
                      </div>
                    </div>
                  )}

                  {/* Verification Details */}
                  {submission.verification_status !== 'PENDING' && (
                    <div>
                      <h4 className="font-semibold mb-2">{t('workcompliance.verification', 'Verification')}</h4>
                      <div className="space-y-1 text-sm">
                        <p>Verified: {submission.verified_date ? fmt.short(submission.verified_date) : 'N/A'}</p>
                        {submission.verified_by_name && <p>By: {submission.verified_by_name}</p>}
                        {submission.verification_notes && (
                          <p className="mt-2 p-2 bg-gray-50 rounded">{submission.verification_notes}</p>
                        )}
                        {submission.rejection_reason && (
                          <p className="mt-2 p-2 bg-red-50 text-red-800 rounded">
                            Rejection Reason: {submission.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Milestones */}
                  {submission.milestones_triggered && submission.milestones_triggered.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">{t('workcompliance.milestones_earned', 'Milestones Earned 🎉')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {submission.milestones_triggered.map((milestone: string, idx: number) => (
                          <Badge key={idx} className="bg-green-100 text-green-800">
                            ✓ {milestone}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default MemberComplianceHistory;
